import React from 'react'
import BootstrapNav from 'react-bootstrap/Nav'
import BSNavDropdown from 'react-bootstrap/NavDropdown'
import Navbar from 'react-bootstrap/Navbar'
import Container from 'react-bootstrap/Container'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import NavLink from './NavLink'
import Icon from './NavIcon'
import NavDropdown from './NavDropdown'
import NestedDropdown from './NestedDropdown'
import { itemStyle } from './styles'
import { NavItem as NavItemType, ParamSlug } from '../../types'

import AmountOfNotifications from '../../features/chat/components/AmountOfNotifications'
import { _ } from '../../utils/localization'
import openChatModal from '../../features/chat/openChatModal'
import useHomeLink from '../../hooks/useHomeLink'
import { navSelector } from '../../features/events/navigationSlice'
import getPathForSpace from '../../utils/spaces/getPathForSpace'
import useParams from '../../hooks/useParams'
import { RootState } from '../../store'
import styles from '../../constants/styles'
import ProfileDropdown from './components/ProfileDropdown'
import { selectTotalUnread } from '../../features/chat/chatSlice'
import useFloorMapSpace from '../../hooks/useFloorMapLink'
import FullScreenToggle from './components/FullScreenToggle'
import HelpContent from '../../features/helpcontent/HelpContent'
import useSlug from '../../hooks/useSlug'

const S = {
    Navbar: styled(Navbar)`
        box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.25);
    `,
    FloormapWrapper: styled.div`
        display: flex;
        height: ${styles.topNavHeight}px;
        justify-content: center;
        align-items: center;
        border-left: 1px solid #e3e3e3;
        border-right: 1px solid #e3e3e3;
    `,
    ItemDivider: styled.span`
        border-left: rgba(0, 0, 0, 0.3) 1px solid;
        margin-top: -15px;
        margin-bottom: -15px;
    `,
    NavItemsWrapper: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 100%;
        flex-grow: 1;
    `,
    RightItemsWrapper: styled.div`
        display: flex;
        height: ${styles.topNavHeight}px;
        justify-content: space-between;
        align-items: center;
    `,
    Logo: styled.img`
        padding: 6px 0;
        height: 56px;
        max-width: 220px;
        object-fit: contain;
    `,
    DropDownItem: styled(BSNavDropdown.Item)`
        padding: 0;
        &:active, &:focus {
            background-color: inherit!important;
        }
    `,
}

const MessagesToggle = () => {
    const eventSlug = useSlug('eventSlug')
    const dispatch = useDispatch()
    const totalUnread = useSelector(selectTotalUnread)
    const onClick = () => {
        dispatch(openChatModal(eventSlug))
    }
    return (
        <BootstrapNav.Item onClick={onClick} style={itemStyle} className="nav-link">
            <Icon icon="message" />
            <span>{ _('nav.messages') }</span>
            <AmountOfNotifications amount={totalUnread} />
        </BootstrapNav.Item>
    )
}

const renderNavItem = (navItem: NavItemType, params: ParamSlug) => (
    <S.DropDownItem key={navItem.id}>
        <NavLink
            text={navItem.text}
            icon={navItem.icon}
            path={getPathForSpace(navItem.space, params)}
            key={navItem.order}
            iconSize={18}
        />
    </S.DropDownItem>
)

const renderNavMenu = (navItem: NavItemType, params: ParamSlug) => (
    <NestedDropdown
        text={navItem.text}
        id="menu"
        icon={navItem.icon}
        key={navItem.id}
    >
        {navItem.children.map((i) => renderNavItem(i, params))}
    </NestedDropdown>
)

const SpacesNavMenu = () => {
    const items = useSelector(navSelector)
    const params = useParams()

    // When navigating from videocall to networking area using the topnav
    // we need to exclude roomId, otherwise we end up in the videocall again.
    if (params.roomId) {
        delete params.roomId
    }

    return (
        <NavDropdown
            text={_('nav.menu')}
            id="menu"
        >
            {
                items.map((item) => (item.children
                    ? renderNavMenu(item, params)
                    : renderNavItem(item, params)))
            }
        </NavDropdown>
    )
}

const Nav = () => {
    const eventSlug = useSlug('eventSlug')
    const homeLink = useHomeLink()
    const floorMapSpace = useFloorMapSpace()
    const event = useSelector((state: RootState) => state.event.current)
    const logo = event?.data?.logo

    // Only render nav after loading event and entering a route with event slug available
    return !(event && eventSlug) ? null : (
        <S.Navbar bg="white" fixed="top" className="p-0">
            <Container>
                <S.NavItemsWrapper>
                    <Navbar.Brand className="p-0">
                        <Link to={homeLink}><S.Logo src={logo} /></Link>
                    </Navbar.Brand>

                    <S.FloormapWrapper>
                        <NavLink
                            text={_('nav.floor_map')}
                            icon="map"
                            iconSize={28}
                            path={floorMapSpace}
                            style={{ justifyContent: 'center', minWidth: 110 }}
                        />
                    </S.FloormapWrapper>

                    <S.RightItemsWrapper>
                        <SpacesNavMenu />
                        <ProfileDropdown />
                        <MessagesToggle />
                        <FullScreenToggle />
                        <HelpContent />
                    </S.RightItemsWrapper>
                </S.NavItemsWrapper>
            </Container>
        </S.Navbar>
    )
}

export default Nav
